import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MainComponent } from './main/main.component';
import { LoginComponent } from './login/login.component';
import { UsersComponent } from './main/settings/users/users.component';
import { UserDetailComponent } from './main/settings/users/detail/detail.component';
import { MajorsComponent } from './main/settings/majors/majors.component';
import { MajorDetailComponent } from './main/settings/majors/detail/detail.component';
import { SubjectsComponent } from './main/settings/subjects/subjects.component';
import { ClassesComponent } from './main/settings/classes/classes.component';
import { StudentsComponent } from './main/settings/students/students.component';
import { StudentFormComponent } from './main/settings/students/form/student-form.component';
import { StudentProfileComponent } from './main/settings/students/profile/student-profile.component';
import { ParentsComponent } from './main/settings/parents/parents.component';
import { ParentProfileComponent } from './main/settings/parents/profile/parent-profile.component';
import { BusesComponent } from './main/settings/buses/buses.component';
import { BusDetailComponent } from './main/settings/buses/detail/bus-detail.component';
import { AcademicYearsComponent } from './main/settings/academic-years/academic-years.component';
import { WeeksComponent } from './main/settings/weeks/weeks.component';
import { SchoolSettingsComponent } from './main/settings/school-settings/school-settings.component';
import { AnnouncementsComponent } from './main/settings/announcements/announcements.component';
import { TeacherAnnouncementsComponent } from './main/announcements/teacher-announcements.component';
import { WeeklyPlansComponent } from './main/weekly-plans/weekly-plans.component';
import { FillDashboardComponent } from './main/dashboard/fill-dashboard.component';
import { AttendanceDashboardComponent } from './main/dashboard/attendance-dashboard.component';
import { AttendanceScannerComponent } from './main/attendance/attendance-scanner.component';
import { ManualAttendanceComponent } from './main/attendance/manual-attendance.component';
import { PickupBoardComponent } from './main/pickup/pickup-board.component';
import { PickupScannerComponent } from './main/pickup/pickup-scanner.component';
import { CanActivateRouteGuard } from './can-activate-route-guard';
import { RoleGuard } from './role-guard';
import { SidebarAccessGuard } from './sidebar-access.guard';
import { AppConstants } from './constants/app-constants';

const routes: Routes = [
  {
    path: '',
    component: LoginComponent,
  },
  {
    path: 'teacher',
    component: MainComponent,
    canActivate: [CanActivateRouteGuard, RoleGuard],
    canActivateChild: [SidebarAccessGuard],
    data: { roles: AppConstants.TEACHER_ROLES, teacherPortal: true },
    children: [
      { path: '', redirectTo: 'weekly-plans', pathMatch: 'full' },
      { path: 'weekly-plans', component: WeeklyPlansComponent, data: { teacherMode: true } },
      { path: 'attendance', component: AttendanceScannerComponent, data: { hideChrome: true } },
      { path: 'attendance-manual', component: ManualAttendanceComponent },
      { path: 'pickup-board', component: PickupBoardComponent },
      { path: 'pickup-scan', component: PickupScannerComponent, data: { hideChrome: true } },
      { path: 'announcements', component: TeacherAnnouncementsComponent },
      ...grantedFeatureRoutes(),
    ],
  },
  {
    path: 'supervisor',
    component: MainComponent,
    canActivate: [CanActivateRouteGuard, RoleGuard],
    canActivateChild: [SidebarAccessGuard],
    data: { roles: AppConstants.SUPERVISOR_ROLES, supervisorPortal: true },
    children: [
      { path: '', redirectTo: 'weekly-plans', pathMatch: 'full' },
      { path: 'weekly-plans', component: WeeklyPlansComponent, data: { supervisorMode: true } },
      { path: 'attendance', component: AttendanceScannerComponent, data: { hideChrome: true } },
      { path: 'attendance-manual', component: ManualAttendanceComponent },
      { path: 'pickup-board', component: PickupBoardComponent },
      { path: 'pickup-scan', component: PickupScannerComponent, data: { hideChrome: true } },
      ...grantedFeatureRoutes(),
    ],
  },
  {
    path: 'main',
    component: MainComponent,
    canActivate: [CanActivateRouteGuard, RoleGuard],
    canActivateChild: [SidebarAccessGuard],
    data: { roles: AppConstants.ADMIN_ROLES },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', component: FillDashboardComponent },
      { path: 'attendance-dashboard', component: AttendanceDashboardComponent },
      { path: 'attendance', component: AttendanceScannerComponent, data: { hideChrome: true } },
      { path: 'attendance-manual', component: ManualAttendanceComponent },
      { path: 'pickup-board', component: PickupBoardComponent },
      { path: 'pickup-scan', component: PickupScannerComponent, data: { hideChrome: true } },
      { path: 'settings/users', component: UsersComponent },
      { path: 'settings/users/:id', component: UserDetailComponent },
      { path: 'settings/majors', component: MajorsComponent },
      { path: 'settings/majors/:id', component: MajorDetailComponent },
      { path: 'settings/subjects', component: SubjectsComponent },
      { path: 'settings/classes', component: ClassesComponent },
      { path: 'settings/students', component: StudentsComponent },
      { path: 'settings/students/new', component: StudentFormComponent },
      { path: 'settings/students/:id/edit', component: StudentFormComponent },
      { path: 'settings/students/:id', component: StudentProfileComponent },
      { path: 'settings/parents', component: ParentsComponent },
      { path: 'settings/parents/:id', component: ParentProfileComponent },
      { path: 'settings/buses', component: BusesComponent },
      { path: 'settings/buses/:id', component: BusDetailComponent },
      { path: 'settings/academic-years', component: AcademicYearsComponent },
      { path: 'settings/weeks', component: WeeksComponent },
      { path: 'settings/school', component: SchoolSettingsComponent },
      { path: 'settings/announcements', component: AnnouncementsComponent },
      { path: 'weekly-plans', component: WeeklyPlansComponent },
    ],
  },
  {
    path: '**',
    redirectTo: 'main'
  }
];

/** Pages an admin can grant on a teacher or supervisor sidebar. */
function grantedFeatureRoutes(): Routes {
  return [
    { path: 'dashboard', component: FillDashboardComponent },
    { path: 'attendance-dashboard', component: AttendanceDashboardComponent },
    { path: 'settings/users', component: UsersComponent },
    { path: 'settings/users/:id', component: UserDetailComponent },
    { path: 'settings/majors', component: MajorsComponent },
    { path: 'settings/majors/:id', component: MajorDetailComponent },
    { path: 'settings/subjects', component: SubjectsComponent },
    { path: 'settings/classes', component: ClassesComponent },
    { path: 'settings/students', component: StudentsComponent },
    { path: 'settings/students/new', component: StudentFormComponent },
    { path: 'settings/students/:id/edit', component: StudentFormComponent },
    { path: 'settings/students/:id', component: StudentProfileComponent },
    { path: 'settings/parents', component: ParentsComponent },
    { path: 'settings/parents/:id', component: ParentProfileComponent },
    { path: 'settings/buses', component: BusesComponent },
    { path: 'settings/buses/:id', component: BusDetailComponent },
    { path: 'settings/academic-years', component: AcademicYearsComponent },
    { path: 'settings/weeks', component: WeeksComponent },
    { path: 'settings/school', component: SchoolSettingsComponent },
    { path: 'settings/announcements', component: AnnouncementsComponent },
  ];
}

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [RoleGuard, SidebarAccessGuard]
})
export class AppRoutingModule { }
